// =============================================
// SchemaReadinessBadge — inline badge per node w ContentEnginePanel.
// Pokazuje dry-run schema.org readiness (ready/partial/off/invalid).
// =============================================

import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { FileCode2, CheckCircle2, AlertTriangle, MinusCircle, XCircle, Loader2 } from "lucide-react";
import { useSchemaReadiness, type SchemaReadinessLevel } from "@/hooks/useSchemaReadiness";

const ICON_MAP: Record<SchemaReadinessLevel, React.ComponentType<{ className?: string }>> = {
  ready: CheckCircle2,
  partial: AlertTriangle,
  off: MinusCircle,
  invalid: XCircle,
  loading: Loader2,
};

interface SchemaReadinessBadgeProps {
  nodeId: string | null;
  compact?: boolean;
}

export function SchemaReadinessBadge({ nodeId, compact = false }: SchemaReadinessBadgeProps) {
  const { data, isLoading } = useSchemaReadiness(nodeId);

  if (isLoading || !data) {
    return (
      <Badge variant="outline" className="h-5 text-[10px] px-1.5 gap-1 font-normal">
        <Loader2 className="h-2.5 w-2.5 animate-spin" />
        {!compact && <span>schema</span>}
      </Badge>
    );
  }

  const Icon = ICON_MAP[data.level] || FileCode2;
  const spinning = data.level === "loading";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Badge
            variant="outline"
            className={`h-5 text-[10px] px-1.5 gap-1 font-normal cursor-help border ${data.color}`}
          >
            <Icon className={`h-2.5 w-2.5 ${spinning ? "animate-spin" : ""}`} />
            {!compact && <span>{data.label}</span>}
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <div className="space-y-1">
          <p className="text-xs font-semibold">{data.label}</p>
          {data.reasons.length > 0 ? (
            <ul className="text-[11px] text-muted-foreground list-disc list-inside space-y-0.5">
              {data.reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          ) : data.level === "ready" ? (
            <p className="text-[11px] text-muted-foreground">
              Wszystkie dane schema.org kompletne — publish wstrzyknie pełny zestaw JSON-LD.
            </p>
          ) : null}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
